import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  Trash2,
  UserCog,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { tenantKeys, tenantMemberKeys } from "@/lib/queryKeys";
import { updateTenant } from "@/services/authService";
import {
  cancelTenantInvite,
  createTenantInvite,
  listTenantMembers,
  listTenantPendingInvites,
  type TenantMember,
  type TenantPendingInvite,
} from "@/services/tenantUsersService";

const PENDING_INVITES_QUERY_KEY = ["tenant-pending-invites"] as const;

/**
 * Accountant-handoff banner for a delegated owner — a business owner who chose
 * "my accountant continues" on onboarding, paid for their plan, and now waits
 * for the accountant to finish the technical setup from their cartera.
 *
 * Rendered INSIDE the onboarding checklist card (DashboardOnboardingCard in its
 * `delegated` mode), right under the X/Y hero, so the owner sees the full step
 * list (and can act on the steps that are theirs to do) with a clear notice
 * that the technical steps are being handled by their accountant. This is the
 * banner form of the old standalone DashboardAccountantHandoffCard.
 *
 * Three states, each with its own copy so it never claims something untrue:
 *   · joined   — the accountant accepted; they're configuring. Show who.
 *   · invited  — invite sent, not yet accepted. Show the email + cancel,
 *                and a "wrong email?" re-invite affordance.
 *   · none     — no pending invite (edge: send failed / cancelled). Prompt.
 * A loading state covers the initial fetch so we don't flash the "none"
 * form before the invite query resolves.
 *
 * Always offers a "do it myself" escape that flips onboardingMode back to
 * self and resumes the wizard (plan already paid, so it skips the gate).
 */
export function DashboardAccountantHandoffBanner(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();
  const tenantId = user?.tenantId ?? "";

  const [email, setEmail] = useState("");
  const [showReinvite, setShowReinvite] = useState(false);
  const [cancelInviteId, setCancelInviteId] = useState<string | null>(null);
  const [escapeOpen, setEscapeOpen] = useState(false);
  const [escaping, setEscaping] = useState(false);

  const {
    data: pendingInvites = [],
    isLoading: invitesLoading,
    refetch: refetchPendingInvites,
  } = useQuery({
    queryKey: [...PENDING_INVITES_QUERY_KEY, tenantId],
    queryFn: () => listTenantPendingInvites(tenantId),
    enabled: Boolean(tenantId),
  });
  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: tenantMemberKeys.list(tenantId),
    queryFn: () => listTenantMembers(tenantId),
    enabled: Boolean(tenantId),
  });

  const accountantMembers = members.filter(
    (m: TenantMember) => m.role === "accountant",
  );
  const accountantInvites = pendingInvites.filter(
    (i: TenantPendingInvite) => i.role === "accountant",
  );
  const hasAccountant = accountantMembers.length > 0;
  const isLoading = invitesLoading || membersLoading;

  const memberLabel = (m: TenantMember): string =>
    m.displayName?.trim() ||
    [m.firstName, m.lastName].filter(Boolean).join(" ").trim() ||
    m.email;

  const inviteMutation = useMutation({
    mutationFn: (payload: { email: string }) =>
      createTenantInvite(tenantId, {
        email: payload.email.trim().toLowerCase(),
        role: "accountant",
      }),
    onSuccess: () => {
      toast.success(t("onboarding.inviteStep.inviteSuccess"));
      setEmail("");
      setShowReinvite(false);
      void queryClient.invalidateQueries({
        queryKey: [...PENDING_INVITES_QUERY_KEY, tenantId],
      });
      void refetchPendingInvites();
    },
    onError: () => {
      toast.error(t("onboarding.inviteStep.inviteError"));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (inviteId: string) => cancelTenantInvite(tenantId, inviteId),
    onSuccess: () => {
      setCancelInviteId(null);
      void queryClient.invalidateQueries({
        queryKey: [...PENDING_INVITES_QUERY_KEY, tenantId],
      });
      void refetchPendingInvites();
      toast.success(t("users.invite.cancelSuccess"));
    },
    onError: () => {
      toast.error(t("users.invite.cancelError"));
    },
  });

  const handleInvite = (e: React.FormEvent): void => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    inviteMutation.mutate({ email: trimmed });
  };

  /** "I'll do it myself": flip the tenant back to self-onboarding and resume
   *  the wizard. The plan is already paid (planId set) so the plan gate is
   *  skipped — the owner lands directly in the technical setup steps. */
  const handleEscape = async (): Promise<void> => {
    setEscaping(true);
    try {
      await updateTenant({ onboardingMode: "self" });
      await refreshUser();
      await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      navigate("/onboarding", { replace: true });
    } catch {
      toast.error(t("onboarding.errorSave"));
      setEscaping(false);
    }
  };

  const isSending = inviteMutation.isPending;

  const title = hasAccountant
    ? t(
        "dashboard.accountantHandoff.titleJoined",
        "Tu contador está configurando Fiscalo",
      )
    : accountantInvites.length > 0
      ? t(
          "dashboard.accountantHandoff.titleInvited",
          "Invitación enviada a tu contador",
        )
      : t(
          "dashboard.accountantHandoff.titleNone",
          "Invitá a tu contador a continuar",
        );

  const lead = hasAccountant
    ? t(
        "dashboard.accountantHandoff.joinedLead",
        "Tu contador ya tiene acceso y está completando la configuración técnica (acceso DGI, CAEs y certificado). No tenés que hacer nada — mientras tanto ya podés usar Fiscalo.",
      )
    : accountantInvites.length > 0
      ? t(
          "dashboard.accountantHandoff.invitedLead",
          "Le enviamos una invitación a tu contador. Cuando la acepte, va a completar la configuración técnica (acceso DGI, CAEs y certificado) por vos. Mientras tanto ya podés usar Fiscalo.",
        )
      : t(
          "dashboard.accountantHandoff.noneLead",
          "Invitá a tu contador para que complete la configuración técnica (acceso DGI, CAEs y certificado). Mientras tanto ya podés usar Fiscalo.",
        );

  const reinviteForm = (
    <form onSubmit={handleInvite} className="space-y-2">
      <Label htmlFor="handoff-invite-email">
        {t("onboarding.inviteStep.emailLabel", "Correo del contador")}
      </Label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="handoff-invite-email"
            type="email"
            placeholder={t(
              "onboarding.inviteStep.emailPlaceholder",
              "contador@email.com",
            )}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-9"
            disabled={isSending}
          />
        </div>
        <Button type="submit" disabled={!email.trim() || isSending}>
          {isSending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("onboarding.inviteStep.sending", "Enviando…")}
            </>
          ) : (
            t("onboarding.inviteStep.sendInvite", "Enviar invitación")
          )}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="overflow-hidden rounded-[var(--fx-r-md)] border border-[var(--fx-ink-100)] bg-white">
      <div className="flex items-start gap-3 border-b border-[var(--fx-ink-100)] bg-[var(--fx-accent-100)] px-5 py-4">
        <span
          aria-hidden
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--fx-accent-500)] text-white"
        >
          <UserCog className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-[var(--fx-ink-900)]">
            {title}
          </h3>
          <p className="mt-1 text-xs leading-relaxed text-[var(--fx-ink-700)]">
            {lead}
          </p>
        </div>
      </div>

      <div className="space-y-4 px-5 py-4">
        {isLoading ? (
          <div className="space-y-3" aria-busy="true">
          <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
          <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
        </div>
      ) : hasAccountant ? (
        <div className="space-y-2">
          {accountantMembers.map((m) => (
            <div
              key={m.userId}
              className="flex items-center gap-3 rounded-md border border-green-500/30 bg-green-500/5 px-4 py-3"
            >
              <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {memberLabel(m)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(
                    "dashboard.accountantHandoff.accountantConfiguring",
                    "Configurando tu empresa",
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : accountantInvites.length > 0 ? (
        <div className="space-y-3">
          <ul className="space-y-2">
            {accountantInvites.map((inv) => (
              <li
                key={inv.id}
                className="flex items-center justify-between gap-3 rounded-md border border-[var(--fx-ink-200)] bg-[var(--fx-paper-2)] px-3 py-2.5 text-sm dark:border-[var(--dk-border)] dark:bg-[var(--dk-surface2)]"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-foreground">{inv.email}</span>
                </span>
                <div className="flex shrink-0 items-center gap-2">
                  <StatusBadge variant="warning">
                    <Clock className="mr-1 h-3 w-3" />
                    {t("users.page.status.pending", "Pendiente")}
                  </StatusBadge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setCancelInviteId(inv.id)}
                    disabled={cancelMutation.isPending}
                  >
                    {cancelMutation.isPending && cancelInviteId === inv.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    {t("users.page.cancelInvite", "Cancelar invitación")}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          {showReinvite ? (
            reinviteForm
          ) : (
            <button
              type="button"
              className="text-sm font-medium text-primary underline-offset-2 hover:underline"
              onClick={() => setShowReinvite(true)}
            >
              {t(
                "dashboard.accountantHandoff.wrongEmail",
                "¿El correo es incorrecto? Invitá a otro contador",
              )}
            </button>
          )}
        </div>
      ) : (
        <div>{reinviteForm}</div>
      )}

        <div className="border-t border-[var(--fx-ink-100)] pt-3">
          <button
            type="button"
            className="text-sm font-medium text-[var(--fx-accent-700)] underline-offset-2 hover:text-[var(--fx-accent-500)] hover:underline disabled:opacity-50"
            onClick={() => setEscapeOpen(true)}
            disabled={escaping}
          >
            {escaping ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("common.loading", "Cargando…")}
              </span>
            ) : (
              t(
                "dashboard.accountantHandoff.doItMyself",
                "Prefiero completar la configuración yo mismo",
              )
            )}
          </button>
        </div>
      </div>

      <ConfirmationDialog
        open={escapeOpen}
        onOpenChange={(open) => !open && setEscapeOpen(false)}
        title={t(
          "dashboard.accountantHandoff.escapeTitle",
          "¿Completar la configuración vos mismo?",
        )}
        description={t(
          "dashboard.accountantHandoff.escapeDescription",
          "Te vamos a guiar paso a paso por la configuración técnica (acceso DGI, certificado, CAEs). Tu plan ya está activo. Podés volver a invitar a tu contador más adelante.",
        )}
        confirmText={t(
          "dashboard.accountantHandoff.escapeConfirm",
          "Sí, lo hago yo",
        )}
        onConfirm={() => {
          setEscapeOpen(false);
          void handleEscape();
        }}
        isLoading={escaping}
      />

      <ConfirmationDialog
        open={cancelInviteId !== null}
        onOpenChange={(open) => !open && setCancelInviteId(null)}
        title={t("users.inviteDetail.cancelTitle")}
        description={t("users.inviteDetail.cancelDescription")}
        confirmText={t("users.page.cancelInvite")}
        variant="destructive"
        onConfirm={() => {
          if (cancelInviteId) cancelMutation.mutate(cancelInviteId);
        }}
        isLoading={cancelMutation.isPending}
      />
    </div>
  );
}
